/**
 * 字幕用的麦克风采集：把本机语音重采样成 16kHz 单声道 PCM，按块回调。
 *
 * 为什么在浏览器做重采样：whisper 只吃 16kHz 单声道，浏览器给的是 48kHz 立体声。
 * 这里有现成的 AudioContext 能直接指定采样率；让服务端再解一遍 Opus 并重采样
 * 不但浪费，还拿不到「是谁说的」——SFU 那侧音频已混在一起，混音识别出来的
 * 字幕没法署名，多人课堂里就没用了。
 */

/** 服务端期望的采样率，与 protocol.CaptionSampleRate 一致。 */
export const CAPTION_SAMPLE_RATE = 16000

/** 每积累多少秒发一块。太碎则进程启动开销占比高，太长则字幕迟。 */
const CHUNK_SECONDS = 1

export interface CaptionCapture {
  stop: () => void
}

/** 把 [-1,1] 的浮点样点转成小端 int16。 */
export function floatToPCM16(samples: Float32Array | number[]): Int16Array {
  const out = new Int16Array(samples.length)
  for (let i = 0; i < samples.length; i++) {
    // 必须先夹到 [-1,1]：超范围的样点直接乘会溢出，听起来是反相爆音，
    // 送进识别则整段变成噪声。
    const v = Math.max(-1, Math.min(1, samples[i]))
    out[i] = v < 0 ? v * 0x8000 : v * 0x7fff
  }
  return out
}

/** 按比例把 src 降采样到 16k。ratio ≤ 1 时原样返回。 */
export function downsample(src: Float32Array, ratio: number): Float32Array {
  if (ratio <= 1.001) return src
  const len = Math.floor(src.length / ratio)
  const out = new Float32Array(len)
  for (let i = 0; i < len; i++) out[i] = src[Math.floor(i * ratio)]
  return out
}

/**
 * 开始采集并按块回调 PCM。
 *
 * @param stream 已有的麦克风流。复用而不是自己再 getUserMedia ——
 *   重复取流会让某些系统出现两个「正在使用麦克风」的指示，用户会以为被偷录。
 */
export async function startCaptionCapture(
  stream: MediaStream,
  onChunk: (pcm: Int16Array) => void,
): Promise<CaptionCapture> {
  if (stream.getAudioTracks().length === 0) throw new Error('这条流里没有音频轨')

  const AC =
    window.AudioContext ||
    (window as never as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
  // 直接让 AudioContext 以 16kHz 运行，省掉手写重采样。
  // Safari 会忽略这个参数，所以下面按实际 sampleRate 再降一次。
  const ctx = new AC({ sampleRate: CAPTION_SAMPLE_RATE })
  const src = ctx.createMediaStreamSource(stream)

  // 用 ScriptProcessor 而不是 AudioWorklet：后者需要单独的模块文件，
  // 而前端是打包产物，动态加载 worklet 路径在子目录部署下容易失效。
  // 采集 PCM 这点计算量 ScriptProcessor 完全够用。
  const node = ctx.createScriptProcessor(4096, 1, 1)
  const ratio = ctx.sampleRate / CAPTION_SAMPLE_RATE
  const target = Math.floor(CAPTION_SAMPLE_RATE * CHUNK_SECONDS)
  let acc: number[] = []

  node.onaudioprocess = (e) => {
    const ds = downsample(e.inputBuffer.getChannelData(0), ratio)
    for (let i = 0; i < ds.length; i++) acc.push(ds[i])
    while (acc.length >= target) {
      onChunk(floatToPCM16(acc.slice(0, target)))
      acc = acc.slice(target)
    }
  }

  src.connect(node)
  // 必须接到 destination 才会被调度，但不能真出声（否则自己听到自己），
  // 所以中间串一个静音增益。
  const mute = ctx.createGain()
  mute.gain.value = 0
  node.connect(mute)
  mute.connect(ctx.destination)

  return {
    stop: () => {
      node.onaudioprocess = null
      try {
        src.disconnect()
        node.disconnect()
        mute.disconnect()
      } catch {
        // 已经断开了
      }
      // 必须 close：浏览器对同时存在的 AudioContext 有数量上限，
      // 泄漏几次之后新的音频功能会静默失效（speakMeter 那边踩过同样的坑）。
      void ctx.close()
    },
  }
}
